import { useCallback } from "react";
import { API_BASE_URL } from "@/constants/app.constant";
import { useFetch } from "@/hooks/useFetch";
import useAuthService from "@/services/auth.service";
import { APIResponse } from "@/interfaces/common.interface";
import { FeedbackDailyCount } from "@/interfaces/chat-metrics.interface";

const useChatMetricsService = () => {
  const fetchService = useFetch();
  const { getUserAccessToken } = useAuthService();

  const getDailyCounts = useCallback(
    async (days: number = 30, conversationTypes?: string[], signal?: AbortSignal): Promise<APIResponse<FeedbackDailyCount[]>> => {
      const token = await getUserAccessToken();
      if (!token) {
        throw new Error("Invalid token");
      }
      let url = `${API_BASE_URL}/admin/feedback/daily-counts?days=${days}`;
      if (conversationTypes && conversationTypes.length > 0) {
        conversationTypes.forEach((t) => { url += `&conversation_type=${t}`; });
      }
      const response = await fetchService.get<APIResponse<FeedbackDailyCount[]>>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        signal,
      });
      return response;
    },
    [fetchService, getUserAccessToken]
  );

  const getTotalUniqueUsers = useCallback(
    async (days: number = 30, conversationTypes?: string[], signal?: AbortSignal): Promise<APIResponse<number>> => {
      const token = await getUserAccessToken();
      if (!token) {
        throw new Error("Invalid token");
      }
      let url = `${API_BASE_URL}/admin/feedback/total-unique-users?days=${days}`;
      if (conversationTypes && conversationTypes.length > 0) {
        conversationTypes.forEach((t) => { url += `&conversation_type=${t}`; });
      }
      const response = await fetchService.get<APIResponse<number>>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        signal,
      });
      return response;
    },
    [fetchService, getUserAccessToken]
  );

  return { getDailyCounts, getTotalUniqueUsers };
};

export default useChatMetricsService;
